<p-toast></p-toast>
@if (isLoading) {
<p-progressBar mode="indeterminate" class="m-2"></p-progressBar>
}
<app-page-shell>

    <app-page-hero
        eyebrow="CRM Contracts"
        [title]="createEditRateCard.rateId ? 'Edit Delivery Rate Card' : 'Create Delivery Rate Card'"
        subtitle="Maintain route, vehicle, and charge rules for the selected contract revision.">
        <div actions>
            <p-button label="Clear" icon="fa-solid fa-xmark" severity="secondary" [outlined]="true" (onClick)="clearRateCardForm()"></p-button>
            <p-button [label]="createEditRateCard.rateId ? 'Save Changes' : 'Create Rate Card'"
                [icon]="createEditRateCard.rateId ? 'fa-solid fa-floppy-disk' : 'fa-solid fa-plus'"
                [loading]="rateCardCreationInProgress" (onClick)="createUpdateRateCard()"></p-button>
        </div>
    </app-page-hero>

    <form class="overFlow" [formGroup]="createEditRateCardForm" novalidate autocomplete="off" autocorrect="off"
        autocapitalize="off" spellcheck="off">
        <div class="p-fluid flex flex-column gap-3">

            <!-- Vehicle Specifications Fieldset -->
            <app-page-form-section icon="fa-solid fa-truck-fast" title="Vehicle Specifications" subtitle="Select vehicle type, configuration, and capacity for this rate.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-truck-fast"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select id="vehicleType" [options]="vehicleTypes"
                                    [(ngModel)]="createEditRateCard.vehicleTypeId" optionLabel="name" [filter]="true"
                                    filterBy="name" [showClear]="true" formControlName="vehicleTypeDropdown"
                                    styleClass="w-full" (onChange)="onVehicleTypeChange()">
                                    <!-- Filter and Item Templates -->
                                </p-select>
                                <label for="vehicleType">Vehicle Type</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-cogs"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select id="vehicleConfig" [options]="filteredVehicleConfigs"
                                    [(ngModel)]="createEditRateCard.vehicleConfigId" optionLabel="name" [filter]="true"
                                    filterBy="name" [showClear]="true" formControlName="vehicleConfigDropdown"
                                    styleClass="w-full">
                                    <!-- Filter and Item Templates -->
                                </p-select>
                                <label for="vehicleConfig">Vehicle Configuration</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-4">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-weight-hanging"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select id="vehicleCapacity" [options]="filteredVehicleCapacities"
                                    [(ngModel)]="createEditRateCard.vehicleCapacityId" optionLabel="name"
                                    [filter]="true" filterBy="name" [showClear]="true"
                                    formControlName="vehicleCapacityDropdown" styleClass="w-full">
                                    <!-- Filter and Item Templates -->
                                </p-select>
                                <label for="vehicleCapacity">Vehicle Capacity</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <!-- Route Details Fieldset -->
            <app-page-form-section icon="fa-solid fa-route" title="Route Details" subtitle="Define source and destination coverage for this contract rate.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-earth-americas"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select id="sourceCountry" [options]="countries" optionLabel="name"
                                    optionValue="id" [filter]="true" filterBy="name,countryCode"
                                    [(ngModel)]="createEditRateCard.sourceCountryId" formControlName="sourceCountryInput"
                                    (onChange)="onSourceCountryChange()"
                                    styleClass="w-full"></p-select>
                                <label for="sourceCountry">Source Country</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-city"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select id="sourceCity" [options]="sourceCities" optionLabel="cityName"
                                    [filter]="true" filterBy="cityName,country.name,country.countryCode"
                                    formControlName="sourceCityInput" [disabled]="!createEditRateCardForm.get('sourceCountryInput')?.value"
                                    styleClass="w-full"></p-select>
                                <label for="sourceCity">Source City</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-earth-americas"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select id="destinationCountry" [options]="countries" optionLabel="name"
                                    optionValue="id" [filter]="true" filterBy="name,countryCode"
                                    [(ngModel)]="createEditRateCard.destinationCountryId"
                                    (onChange)="onDestinationCountryChange()"
                                    formControlName="destinationCountryInput" styleClass="w-full"></p-select>
                                <label for="destinationCountry">Destination Country</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-city"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select id="destinationCity" [options]="destinationCities" optionLabel="cityName"
                                    [filter]="true" filterBy="cityName,country.name,country.countryCode"
                                    formControlName="destinationCityInput" [disabled]="!createEditRateCardForm.get('destinationCountryInput')?.value"
                                    styleClass="w-full"></p-select>
                                <label for="destinationCity">Destination City</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <!-- Rate & Charges Fieldset -->
            <app-page-form-section icon="fa-solid fa-money-bill-wave" title="Rate & Charges" subtitle="Set contracted amount, distance, waiting threshold, and waiting charge.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-ruler"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber id="distance" [(ngModel)]="createEditRateCard.distance"
                                    formControlName="distanceInput" suffix=" km" styleClass="w-full"></p-inputNumber>
                                <label for="distance">Distance</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-money-bill-wave"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber id="amount" [(ngModel)]="createEditRateCard.amount"
                                    formControlName="amountInput" mode="currency" currency="SAR"
                                    styleClass="w-full"></p-inputNumber>
                                <label for="amount">Amount</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-clock"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber id="waitingThreshold" [(ngModel)]="createEditRateCard.waitingThreshold"
                                    formControlName="waitingThresholdInput" suffix=" hours"
                                    styleClass="w-full"></p-inputNumber>
                                <label for="waitingThreshold">Waiting Threshold</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-money-bill-1-wave"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber id="waitingCharge" [(ngModel)]="createEditRateCard.waitingCharge"
                                    formControlName="waitingChargeInput" mode="currency" currency="SAR"
                                    styleClass="w-full"></p-inputNumber>
                                <label for="waitingCharge">Waiting Charge</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <!-- Additional Terms Fieldset -->
            <app-page-form-section icon="fa-solid fa-circle-check" title="Additional Terms" subtitle="Confirm insurance and port permit inclusions.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-6">
                        <div
                            class="flex justify-content-between align-items-center border-1 surface-border p-3 border-round">
                            <div class="flex align-items-center">
                                <i class="fa-solid fa-shield-halved mr-2 text-xl text-color-secondary"></i>
                                <label for="goodsInsuranceInput">Goods Insurance Included?</label>
                            </div>
                            <p-toggleswitch id="goodsInsuranceInput" [(ngModel)]="createEditRateCard.goodsInsurance"
                                formControlName="goodsInsuranceInput"></p-toggleswitch>
                        </div>
                    </div>
                    <div class="field col-12 md:col-6">
                        <div
                            class="flex justify-content-between align-items-center border-1 surface-border p-3 border-round">
                            <div class="flex align-items-center">
                                <i class="fa-solid fa-ship mr-2 text-xl text-color-secondary"></i>
                                <label for="portPermitInput">Port Permit Included?</label>
                            </div>
                            <p-toggleswitch id="portPermitInput" [(ngModel)]="createEditRateCard.portPermit"
                                formControlName="portPermitInput"></p-toggleswitch>
                        </div>
                    </div>
                </div>
            </app-page-form-section>
        </div>
    </form>
</app-page-shell>
